/**
 * Il modo in cui il modulo parla con il servizio.
 *
 * Una funzione sola, come nel CRM: gli errori hanno tutti la stessa forma e
 * il messaggio è già scritto per essere letto da una persona.
 */

export class ErroreApi extends Error {
  constructor(
    readonly codice: string,
    messaggio: string,
    readonly stato: number,
    readonly dettagli: Record<string, unknown> = {},
  ) {
    super(messaggio);
    this.name = 'ErroreApi';
  }
}

export async function chiama<T>(
  percorso: string,
  opzioni: { metodo?: string; corpo?: unknown } = {},
): Promise<T> {
  let risposta: Response;
  try {
    risposta = await fetch(percorso, {
      method: opzioni.metodo ?? 'GET',
      credentials: 'same-origin',
      headers: opzioni.corpo === undefined ? {} : { 'content-type': 'application/json' },
      body: opzioni.corpo === undefined ? undefined : JSON.stringify(opzioni.corpo),
    });
  } catch {
    throw new ErroreApi(
      'servizioIrraggiungibile',
      'We could not reach the server. Check your connection and try again.',
      0,
    );
  }

  if (risposta.status === 204) return undefined as T;
  const corpo = await risposta.json().catch(() => null);

  if (!risposta.ok) {
    throw new ErroreApi(
      corpo?.error ?? 'erroreSconosciuto',
      corpo?.message ?? 'Something went wrong.',
      risposta.status,
      (corpo ?? {}) as Record<string, unknown>,
    );
  }
  return corpo as T;
}

export interface Scuola {
  kind: 'highSchool' | 'college' | 'tradeSchool' | 'professionalSchool';
  name?: string | null;
  yearsCompleted?: number | null;
  graduated?: boolean | null;
  subjects?: string | null;
}

export interface Impiego {
  employerName?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  phone?: string | null;
  supervisorName?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
  startSalary?: string | null;
  finalSalary?: string | null;
  jobTitle?: string | null;
  reasonForLeaving?: string | null;
  dutiesAndSkills?: string | null;
}

export interface Referenza {
  name?: string | null;
  role?: string | null;
  company?: string | null;
  address?: string | null;
  addressExtra?: string | null;
  phone?: string | null;
}

export interface Candidatura {
  id: number;
  firstName: string;
  lastName: string;
  middleInitial: string | null;
  email: string;
  phoneAreaCode: string | null;
  phone: string | null;
  addressStreet: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  isOver18: boolean | null;
  isAuthorizedToWork: boolean | null;
  hasDriverLicense: boolean | null;
  positionDesired: string | null;
  availableFrom: string | null;
  salaryDesired: string | null;
  employedNow: boolean | null;
  mayContactEmployer: boolean | null;
  appliedBefore: boolean | null;
  appliedBeforeWhere: string | null;
  appliedBeforeWhen: string | null;
  referredBy: string | null;
  specialSkills: string | null;
  currentCertifications: string | null;
  militaryService: string | null;
  militaryRank: string | null;
  nationalGuardReserves: string | null;
  convictedOfCrime: boolean | null;
  crimeExplanation: string | null;
  emergencyName: string | null;
  emergencyAddress: string | null;
  emergencyPhone: string | null;
  screeningConsentAt: string | null;
  schools: Scuola[];
  employments: Impiego[];
  references: Referenza[];
  files: { id: number; originalName: string; sizeBytes: number }[];
}

export interface Mancanza {
  campo: string;
  testo: string;
}

/** Apre una candidatura nuova; il gettone resta in un cookie. */
export const apriCandidatura = () =>
  chiama<{ token: string }>('/api/applications', { metodo: 'POST' });

export const leggiCandidatura = () => chiama<Candidatura>('/api/applications/mine');

export const salvaCandidatura = (dati: Partial<Candidatura>) =>
  chiama<{ salvato: boolean }>('/api/applications/mine', { metodo: 'PATCH', corpo: dati });

export const dichiaraConsenso = (given: boolean) =>
  chiama<{ given: boolean }>('/api/applications/mine/consent', {
    metodo: 'POST',
    corpo: { given },
  });

/**
 * @param trappola il campo nascosto del modulo: una persona lo lascia vuoto,
 *   un robot no (vedi `candidature-api/src/candidature/robot.ts`).
 */
export const mandaCandidatura = (trappola: string) =>
  chiama<{ ok: true }>('/api/applications/mine/submit', {
    metodo: 'POST',
    corpo: { website: trappola },
  });

/**
 * Il curriculum va per conto suo, con un `FormData`: il confine del multipart
 * lo deve scrivere il browser.
 */
export async function caricaCurriculum(file: File) {
  const dati = new FormData();
  dati.append('file', file);
  const risposta = await fetch('/api/applications/mine/resume', {
    method: 'POST',
    body: dati,
    credentials: 'same-origin',
  });
  const corpo = await risposta.json().catch(() => null);
  if (!risposta.ok) {
    throw new ErroreApi(
      corpo?.error ?? 'erroreCaricamento',
      corpo?.message ?? 'The upload did not go through.',
      risposta.status,
    );
  }
  return corpo as { id: number; originalName: string; sizeBytes: number };
}
